import { alpha, lighten, Theme } from '@material-ui/core/styles';

/**
 * Design tokens shared by the Products plugin and the Hub homepage: one place
 * for the type scale, the two text colours that differ by mode, and the
 * clickable-card behaviour. Colours and shape come from the theme; these
 * tokens only name how the pages use them. If these move into the Hub theme
 * later, this file is the only one to change.
 */

/** Type scale in px. Every font size in these pages is one of these steps. */
export const text = {
  /** Eyebrows, small capitals labels. */
  label: 11,
  /** Meta text: dates, counts, captions. */
  meta: 12,
  /** Secondary text, descriptions, small links. */
  small: 13,
  /** Body text. */
  body: 14,
  /** Slightly emphasised body, list item names. */
  lead: 15,
  /** Introductory paragraphs. */
  emphasis: 16,
  /** Card and panel titles. */
  title: 18,
  /** Section headings. */
  section: 22,
  /** Page headings. */
  page: 26,
  /** Large figures in stat panels. */
  stat: 28,
};

/** Icon sizes in px. */
export const icon = { inline: 13, compact: 16, small: 18, medium: 20 };

/** Navy for headings in light mode; regular text colour in dark mode. */
export const headingColor = (theme: Theme) =>
  theme.palette.type === 'light'
    ? theme.palette.secondary.main
    : theme.palette.text.primary;

/**
 * Teal for links, calls to action and eyebrows. Darker in light mode and
 * lighter in dark mode, so small text meets 4.5:1 on paper in both.
 */
export const linkColor = (theme: Theme) =>
  theme.palette.type === 'light'
    ? theme.palette.primary.dark
    : lighten(theme.palette.primary.main, 0.3);

/** Small-capitals label above a title (business unit, portfolio, category). */
export const eyebrowStyle = (theme: Theme) => ({
  fontSize: text.label,
  fontWeight: 600,
  letterSpacing: '1.1px',
  textTransform: 'uppercase' as const,
  color: linkColor(theme),
});

/** Teal-tinted square behind an icon or initials; readable in both modes. */
export const tintTileStyle = (theme: Theme) => ({
  borderRadius: theme.shape.borderRadius * 2,
  backgroundColor: alpha(theme.palette.primary.main, 0.12),
  color: linkColor(theme),
});

/** The transition every clickable card uses; extend it rather than copy it. */
export const cardTransition =
  'border-color 150ms ease, box-shadow 150ms ease, transform 150ms ease';

/** Two-tone heading sizes: page-level sections and sections within a tab. */
export const headingSize = { section: text.section, subsection: text.title };

/**
 * The one definition of a clickable card's behaviour: hairline border, a teal
 * accent line that draws across the top, and a gentle lift on hover or
 * keyboard focus. Hover applies only on devices with a real pointer, so a tap
 * on a phone never leaves a card stuck raised. Motion is removed for people
 * who ask for reduced motion. Spread it into a makeStyles rule.
 */
export const linkCardStyle = (theme: Theme) => {
  const raised = {
    textDecoration: 'none',
    borderColor: alpha(theme.palette.primary.main, 0.55),
    boxShadow: `0 8px 24px ${alpha(
      theme.palette.common.black,
      theme.palette.type === 'light' ? 0.08 : 0.4,
    )}`,
    transform: 'translateY(-2px)',
  };
  return {
    position: 'relative' as const,
    // Own stacking context, so raised links stay inside the card.
    isolation: 'isolate' as const,
    overflow: 'hidden' as const,
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius * 2,
    // Flat at rest whatever Paper elevation the host theme applies.
    boxShadow: 'none',
    transition: cardTransition,
    '&::before': {
      content: '""',
      position: 'absolute' as const,
      top: 0,
      left: 0,
      right: 0,
      height: 3,
      background: theme.palette.primary.main,
      transform: 'scaleX(0)',
      transformOrigin: 'left',
      transition: 'transform 200ms ease',
    },
    // A card that is itself a link never underlines, on any device.
    '&:hover': { textDecoration: 'none' },
    '&:focus-within': raised,
    '&:focus-within::before': { transform: 'scaleX(1)' },
    // Arrows marked data-arrow nudge right with the lift.
    '& [data-arrow]': {
      display: 'inline-block',
      transition: 'transform 150ms ease',
    },
    '&:focus-within [data-arrow]': { transform: 'translateX(3px)' },
    // The covering title link: heading colour at rest, link colour when the
    // card is raised, never underlined.
    '& [data-stretched]': {
      color: headingColor(theme),
      textDecoration: 'none',
      // Not positioned, or the overlay below stops at the link: Backstage's
      // Link sets position: relative on external links.
      position: 'static' as const,
    },
    '& [data-stretched]:hover': { textDecoration: 'none' },
    '&:focus-within [data-stretched]': { color: linkColor(theme) },
    '@media (hover: hover)': {
      '&:hover': raised,
      '&:hover::before': { transform: 'scaleX(1)' },
      '&:hover [data-arrow]': { transform: 'translateX(3px)' },
      '&:hover [data-stretched]': { color: linkColor(theme) },
    },
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
      '&::before': { transition: 'none' },
      '&:hover, &:focus-within': { transform: 'none' },
      '& [data-arrow]': { transition: 'none' },
      '&:hover [data-arrow], &:focus-within [data-arrow]': {
        transform: 'none',
      },
    },
    // A link marked data-stretched covers the whole card; every other
    // interactive element sits above it and stays clickable.
    '& [data-stretched]::after': {
      content: '""',
      position: 'absolute' as const,
      inset: 0,
    },
    // A plain selector list: JSS splits nested selectors on commas, so :is()
    // would be rewritten into something harder to read.
    '& a:not([data-stretched]), & button, & input, & select, & textarea, & summary, & [role="button"]':
      { position: 'relative' as const, zIndex: 1 },
  };
};
